'use strict';

import Stats from "three/examples/jsm/libs/stats.module";

import { isDev, settingsData, threeJsVariables, TRoot } from '../../settings';
import {PerspectiveCamera_init} from "./camera";
import { addLights, updateShadowMap } from './lights';
import {
    Clock,
    FogExp2,
    PCFSoftShadowMap,
    PMREMGenerator,
    Raycaster,
    Scene,
    WebGLRenderer,
    LinearSRGBColorSpace,
    AxesHelper,
} from 'three';
import { Composer } from '../post-processing/Composer';
import { RenderList } from './RenderList';
import { FPS } from '../gui/FPS';
import { animate } from '../../app';
import { Slicer } from './Slicer';
// import { MiniMap } from '../MiniMap/MiniMap';
import { Tasks } from '../gui/LoaderTask';
import { ClothSimulation } from "./Cloth/ClothSimulation";
import { QA } from '../qa/QA';
import {getUrlParam} from "../../functions";
import { GoogleMiniMap } from '../MiniMap/GoogleMiniMap';
import { OrbitBuildingsNavigation } from '../components/OrbitBuildingsNavigation/OrbitBuildingsNavigation';
import { CssLabelManager } from '../components/CssLabelManager/CssLabelManager';
import { HighQuality } from './Quality/HighQuality';

export const createThreeJsEnvironment = () => {
    let logarithmicDepthBuffer = config.threejs.renderer?.logarithmicDepthBuffer;
    const canvas = document.querySelector('#c');
    const width = window.innerWidth;
    const height = window.innerHeight;
    threeJsVariables.quality = new HighQuality();
    config.resources.cloudflare.settings.web = threeJsVariables.quality.cloudflareParams(config.resources.cloudflare.settings.web);

    canvas.addEventListener('webglcontextcreationerror',function() {
        console.log('create context error');
        webGLRestore(canvas).then(() => window.location.reload(true));
    },{once: true});

    const scene = new Scene();
    // const axesHelper = new AxesHelper(1000);
    // scene.add(axesHelper);
    
    const perspectiveCamera = PerspectiveCamera_init();
    const raycaster = new Raycaster();
    raycaster.firstHitOnly = true;
    raycaster.layers.enable(1);

    let antialias = true

    if (config.resources.bundle.ver==='2.0' || config.threejs.effectComposer?.active) {
        settingsData.postProcessing.isActive = true
        antialias = false
        logarithmicDepthBuffer = false
    }

    const renderer = new WebGLRenderer({
        canvas: canvas,
        antialias: false,
        logarithmicDepthBuffer,
        autoClear: true,
        stencil: false,
        depth: true,
        //precision : 'lowp',
        //powerPreference: 'high-performance', // на некоторых десктоп устройствах вызывает перегрузку GPU, на остальных не дает никакого эффекта
    });

    renderer.setPixelRatio(threeJsVariables.quality.pixelRatio());
    renderer.setSize( width, height );
    renderer.info.autoReset = false;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.autoUpdate = threeJsVariables.quality.autoUpdateShadowMap();
    renderer.outputColorSpace = LinearSRGBColorSpace;
    if (config.resources.bundle.dynamicShadows===true ){
        renderer.shadowMap.autoUpdate = true;
    }
    renderer.sortObjects = false;
    // renderer.shadowMap.type = THREE.VSMShadowMap;
    renderer.shadowMap.type = PCFSoftShadowMap;
    // renderer.toneMapping = LinearToneMapping;
    renderer.setClearColor( 0x000000, 1 );

    const context = renderer.getContext();
    let contextRestored = false;

    if (context) {

        context.canvas.addEventListener("webglcontextrestored", function(event) {
            console.log('context restored');
            animate();
            updateShadowMap();
            contextRestored = true;
        }, false);

        context.canvas.addEventListener("webglcontextlost", function(event) {
            event.preventDefault();
            console.log('context lost');
            contextRestored = false;
            cancelAnimationFrame(settingsData.renderAnim);
            setTimeout(() => {
                if (!contextRestored) {
                    window.location.reload(true);
                }
            },5000);
        }, false);
    }

    scene.fog = new FogExp2( 0xffffff, 0.0 )

    
    const cloth = new ClothSimulation();

    threeJsVariables.scene = scene;
    threeJsVariables.renderer = renderer;
    threeJsVariables.raycaster = raycaster;
    // threeJsVariables.scene.background = new THREE.Color(0x151515);
    threeJsVariables.pmremgenerator = new PMREMGenerator(renderer);
    threeJsVariables.renderList = new RenderList(scene,renderer);
    threeJsVariables.slicer = new Slicer(renderer,config.threejs.slicer);
    threeJsVariables.sceneObjectsSlicer = new Slicer(renderer,config.threejs.sceneObjectsSlicer);
    threeJsVariables.fps = new FPS();
    threeJsVariables.tasks = new Tasks();
    threeJsVariables.orbitBuildingsNavigation = new OrbitBuildingsNavigation();
    // threeJsVariables.miniMap = new MiniMap(scene,renderer);
    threeJsVariables.googleMiniMap = new GoogleMiniMap();
    threeJsVariables.cssLabelManager = new CssLabelManager();
    
    // threeJsVariables.miniMap.appendTo(TRoot);
    // threeJsVariables.miniMap.hide();

    threeJsVariables.perspectiveCamera = perspectiveCamera;
    threeJsVariables.composer = new Composer(renderer,scene,perspectiveCamera);
    threeJsVariables.cloth = cloth;

    //directionlight();
    addLights();

    threeJsVariables.time = new Clock();

    // settingsData.postProcessing.isActive && postProcessing(renderer,perspectiveCamera,canvas,scene)

    if (getUrlParam('dev')){
        addRendererInfoGui();
    }
};


function webGLRestore (canvas) {
    return new Promise(resolve => {
       const waitingWebGL = setInterval(() => {
           console.log(console.warn('Waiting WEBGL restore'));
           if (!!window.WebGLRenderingContext &&
               (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))) {
               clearInterval(waitingWebGL);
               resolve();
           }
       },5000)
    });
}

function parallelTraverse( a, b, callback ) {

	callback( a, b );

	for ( let i = 0; i < a.children.length; i ++ ) {

		parallelTraverse( a.children[ i ], b.children[ i ], callback );

	}

}

export const fullClone = ( source )=> {

	const sourceLookup = new Map()
	const cloneLookup = new Map()

	const clone = source.clone()

	parallelTraverse( source, clone, function ( sourceNode, clonedNode ) {

		sourceLookup.set( clonedNode, sourceNode )
		cloneLookup.set( sourceNode, clonedNode )

	} )

	clone.traverse( function ( node ) {

		if ( ! node.isSkinnedMesh ) return

		const clonedMesh = node;
		const sourceMesh = sourceLookup.get( node )
		const sourceBones = sourceMesh.skeleton.bones

		clonedMesh.skeleton = sourceMesh.skeleton.clone()
		clonedMesh.bindMatrix.copy( sourceMesh.bindMatrix )

		clonedMesh.skeleton.bones = sourceBones.map( function ( bone ) {

			return cloneLookup.get( bone )

		} );

		clonedMesh.bind( clonedMesh.skeleton, clonedMesh.bindMatrix )

	} )

    if (source.animations){
        clone.animations = source.animations
    }

	return clone

}

function addRendererInfoGui () {
    const {gui} = threeJsVariables;
    let loop;

    const params = {
        geometries: 0,
        textures: 0,
        triangles: 0,
        tiles: settingsData.totalTilesCount
    };

    const folder = gui.addFolder('Renderer info');

    folder.add( params, 'geometries');
    folder.add( params, 'textures');
    folder.add( params, 'triangles');
    folder.add(params,'tiles');

    const controllers = threeJsVariables.gui.__folders["Renderer info"].__controllers;

    function infoLoop () {
        controllers.forEach(control =>{
            control.object = {
                geometries: threeJsVariables.renderer.info.memory.geometries,
                textures: threeJsVariables.renderer.info.memory.textures,
                triangles: threeJsVariables.renderer.info.render.triangles,
                tiles: settingsData.totalTilesCount
            } ;
            control.updateDisplay();
        });

        loop = requestAnimationFrame(infoLoop);
    }

    infoLoop();
}

export function setStats() {
    if (!isDev && !QA.active) return;

    const stats = new Stats();
    stats.dom.style.top = '20px';
    stats.dom.style.left = '7%';

    threeJsVariables.stats = stats;

    document.body.insertAdjacentElement('afterbegin', stats.dom);
}




